import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const entrySchema = yup.object().shape({
    userId: yup.string().required('Пользователь обязателен'),
    shiftDate: yup.date().required('Дата смены обязательна'),
    entryDate: yup.date().required('Дата входа обязательна'),
    entryTime: yup.string().required('Время входа обязательно'),
    shiftSchedule: yup.string().required('Смена обязательна'),
    description: yup.string()
});

const exitSchema = yup.object().shape({
    exitDate: yup.date().required('Дата выхода обязательна'),
    exitTime: yup.string().required('Время выхода обязательно'),
    description: yup.string()
});

const WorkShiftLogs = () => {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isExitModalOpen, setIsExitModalOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [users, setUsers] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    const { logout, hasPermission } = useUser();
    const canView = hasPermission("shift_logs", "view");
    const canCreate = hasPermission("shift_logs", "create");
    const canEdit = hasPermission("shift_logs", "edit");

    const {
        register: registerEntry,
        handleSubmit: handleSubmitEntry,
        reset: resetEntry,
        formState: { errors: entryErrors }
    } = useForm({
        resolver: yupResolver(entrySchema)
    });

    const {
        register: registerExit,
        handleSubmit: handleSubmitExit,
        reset: resetExit,
        formState: { errors: exitErrors }
    } = useForm({
        resolver: yupResolver(exitSchema)
    });

    useEffect(() => {
        fetchLogs();
        fetchUsers();
        fetchShifts();
    }, [page, selectedDate]);

    const fetchLogs = async () => {
        try {
			const localDate = new Date(selectedDate);
			localDate.setHours(0, 0, 0, 0);
			const formattedDate = selectedDate;

            const params = new URLSearchParams({
                page,
                limit,
                date: formattedDate
            });

            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/work-shift-logs?${params}`,
                {},
                logout
            );
            const data = await response.json();
            setLogs(data.logs);
            setTotal(data.total);
        } catch (error) {
            console.error('Ошибка при получении логов:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/users`,
                {},
                logout
            );
            const data = await response.json();
            setUsers(data.users);
        } catch (error) {
            console.error('Ошибка при получении пользователей:', error);
        }
    };

    const fetchShifts = async () => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/shift-schedules`,
                {},
                logout
            );
            const data = await response.json();
            setShifts(data);
        } catch (error) {
            console.error('Ошибка при получении смен:', error);
        }
    };

    const handleDeleteLog = async (logId) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот лог?')) return;

        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/work-shift-logs/${logId}`,
                {
                    method: 'DELETE',
                },
                logout
            );

            if (response.ok) {
                fetchLogs();
            }
        } catch (error) {
            console.error('Ошибка при удалении лога:', error);
        }
    };

    const handleCreateLog = async (data) => {
        try {
			const entryDate = new Date(data.entryDate);
            const [hours, minutes] = data.entryTime.split(':');
            entryDate.setHours(hours, minutes, 0, 0);
			
			 const raw = data.shiftDate instanceof Date
			 ? data.shiftDate
			 : new Date(data.shiftDate); 
		 	 
		    raw.setHours(12, 0, 0, 0);		    
		    const dateToSend = raw.toISOString(); // отправим ISO, например "2025-04-22T05:00:00.000Z"
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/work-shift-logs`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userId: data.userId,
                        date: dateToSend,
                        enteredAt: entryDate.toISOString(),
                        shiftSchedule: data.shiftSchedule,
                        description: data.description
                    }),
                },
                logout
            );

            if (response.ok) {
                fetchLogs();
                setIsModalOpen(false);
                resetEntry();
            }
        } catch (error) {
            console.error('Ошибка при создании лога:', error);
        }
    };

    const handleUpdateLog = async (data) => {
        try {
            const exitDate = new Date(data.exitDate);
            const [hours, minutes] = data.exitTime.split(':');
            exitDate.setHours(hours, minutes, 0, 0);

            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/work-shift-logs/${selectedLog._id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        exitedAt: exitDate.toISOString(),
                        description: data.description
                    }),
                },
                logout
            );

            if (response.ok) {
                fetchLogs();
                setIsExitModalOpen(false);
                setSelectedLog(null);
                resetExit();
            }
        } catch (error) {
            console.error('Ошибка при обновлении лога:', error);
        }
    };

    if (!canView) {
        return <div className="text-red-500 text-center text-2xl">У вас нет прав на просмотр логов</div>;
    }

    return (

		<div className="space-y-4">
            <div className="flex justify-between items-center">
                <div className="flex gap-4">
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                    />
                    {canCreate && (
                        <Button onClick={() => setIsModalOpen(true)} variant="primary">
                            Отметить вход
                        </Button>
                    )}
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full table-auto border border-[#3E3E42]">
                    <thead>
                        <tr className="bg-[#252526]">
                            <th className="p-2 border border-[#3E3E42]">Пользователь</th>
                            <th className="p-2 border border-[#3E3E42]">Дата смены</th>
                            <th className="p-2 border border-[#3E3E42]">Смена</th>
                            <th className="p-2 border border-[#3E3E42]">Вход</th>
                            <th className="p-2 border border-[#3E3E42]">Выход</th>
                            <th className="p-2 border border-[#3E3E42]">Метод входа</th>
                            <th className="p-2 border border-[#3E3E42]">Метод выхода</th>
                            <th className="p-2 border border-[#3E3E42]">Описание</th>
                            <th className="p-2 border border-[#3E3E42]">Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log) => (
                            <tr key={log._id} className="text-sm">
                                <td className="p-2 border border-[#3E3E42]">
                                    {log.userId?.fullName} ({log.userId?.username})
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {new Date(log.date).toLocaleDateString()}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {log.shiftSchedule?.name}({log.shiftSchedule?.startTime} - {log.shiftSchedule?.endTime})
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {log.enteredAt
                                        ? new Date(log.enteredAt).toLocaleString('ru-RU', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })
                                        : '-'}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {log.exitedAt
                                        ? new Date(log.exitedAt).toLocaleString('ru-RU', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })
                                        : '-'}
                                </td>
								<td className="p-2 border border-[#3E3E42]">
									{log.entryMethod === 'face' ? 'Лицо' : log.entryMethod === 'nfc' ? 'NFC' : 'Ручной'}
								</td>
								<td className="p-2 border border-[#3E3E42]">
									{log.exiteMethod === 'face' ? 'Лицо' : log.exiteMethod === 'nfc' ? 'NFC' : 'Ручной'}
								</td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {log.description}
                                </td>
                                <td className="p-2 border border-[#3E3E42] flex gap-2">
                                    {canEdit && !log.exitedAt && (
                                        <Button
                                            onClick={() => {
                                                setSelectedLog(log);
                                                setIsExitModalOpen(true);
                                            }}
                                            variant="primary"
                                        >
                                            Отм.выход
                                        </Button>
                                    )}
                                    {canEdit && (
                                        <Button
                                            onClick={() => handleDeleteLog(log._id)}
                                            variant="danger"
                                        >
                                            Удалить
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-center gap-2">
                {Array.from({ length: Math.ceil(total / limit) }, (_, i) => (
                    <Button
                        key={i}
                        onClick={() => setPage(i + 1)}
                        variant={page === i + 1 ? "primary" : "secondary"}
                    >
                        {i + 1}
                    </Button>
                ))}
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    resetEntry();
                }}
                title="Отметить вход вручную"
            >
                <form onSubmit={handleSubmitEntry(handleCreateLog)} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Пользователь</label>
                        <select
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...registerEntry('userId')}
                        >
                            <option value="">Выберите пользователя...</option>
                            {users.map((user) => (
                                <option key={user._id} value={user._id}>
                                    {user.fullName || user.username}
                                </option>
                            ))}
                        </select>
                        {entryErrors.userId && (
                            <p className="text-red-500 text-sm">{entryErrors.userId.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Дата смены</label>
                        <input
                            type="date"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...registerEntry('shiftDate')}
                        />
                        {entryErrors.shiftDate && (
                            <p className="text-red-500 text-sm">{entryErrors.shiftDate.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Дата входа</label>
                        <input
                            type="date"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...registerEntry('entryDate')}
                        />
                        {entryErrors.entryDate && (
                            <p className="text-red-500 text-sm">{entryErrors.entryDate.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Время входа</label>
                        <input
                            type="time"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...registerEntry('entryTime')}
                        />
                        {entryErrors.entryTime && (
                            <p className="text-red-500 text-sm">{entryErrors.entryTime.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Смена</label>
                        <select
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...registerEntry('shiftSchedule')}
                        >
                            <option value="">Выберите смену...</option>
                            {shifts.map((shift) => (
                                <option key={shift._id} value={shift._id}>
                                    {shift.name} ({shift.startTime} - {shift.endTime})
                                </option>
                            ))}
                        </select>
                        {entryErrors.shiftSchedule && (
                            <p className="text-red-500 text-sm">{entryErrors.shiftSchedule.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Описание</label>
                        <textarea
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...registerEntry('description')}
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            variant="cancel"
                        >
                            Отмена
                        </Button>
                        <Button type="submit" variant="primary">
                            Добавить
                        </Button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={isExitModalOpen}
                onClose={() => {
                    setIsExitModalOpen(false);
                    setSelectedLog(null);
                    resetExit();
                }}
                title="Отметить выход"
            >
                <form onSubmit={handleSubmitExit(handleUpdateLog)} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Дата выхода</label>
                        <input
                            type="date"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...registerExit('exitDate')}
                        />
                        {exitErrors.exitDate && (
                            <p className="text-red-500 text-sm">{exitErrors.exitDate.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Время выхода</label>
                        <input
                            type="time"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...registerExit('exitTime')}
                        />
                        {exitErrors.exitTime && (
                            <p className="text-red-500 text-sm">{exitErrors.exitTime.message}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Описание</label>
                        <textarea
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...registerExit('description')}
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            onClick={() => {
                                setIsExitModalOpen(false);
                                setSelectedLog(null);
                                resetExit();
                            }}
                            variant="cancel"
                        >
                            Отмена
                        </Button>
                        <Button type="submit" variant="primary">
                            Сохранить
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default WorkShiftLogs; 